import { useState, useEffect, useCallback, useRef, KeyboardEvent } from 'react';

// Types
import { UserProfile } from '@/types';

// Stores
import { useUsers, useIsLoading } from '@/stores';

// Helpers
import {
  findExactUsernameMatch,
  getUserMentionText,
  normalizeQuery,
} from '@/components/UserMentionDropdown/helpers';

// Constants
import { KEYBOARD_EVENT } from '@shared/constants';

const DEFAULT_MENTION_STATE = {
  isActive: false,
  query: '',
  startPosition: -1,
  endPosition: -1,
};

export const useMentionUsers = () => {
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [selectedUserIndex, setSelectedUserIndex] = useState(-1);
  const [mentionState, setMentionState] = useState(DEFAULT_MENTION_STATE);

  const selectedUserRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const users = useUsers();
  const isLoading = useIsLoading();

  // Reset mention state to empty
  const resetMentionState = useCallback(() => {
    setMentionState(DEFAULT_MENTION_STATE);
  }, []);

  // Clear filtered users list when no valid query
  const clearFilteredUsers = useCallback(() => {
    setFilteredUsers([]);
  }, []);

  // Scroll selected user into view
  const scrollSelectedUserIntoView = useCallback(() => {
    setTimeout(() => {
      selectedUserRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 0);
  }, []);

  // Detect @ mentions and update state
  const detectMention = useCallback(
    (value: string, cursorPosition: number) => {
      // Find the last @ before cursor
      const beforeCursor = value.substring(0, cursorPosition);
      const lastAtIndex = beforeCursor.lastIndexOf('@');

      // No @ found, deactivate
      if (lastAtIndex === -1) {
        resetMentionState();
        return;
      }

      // Check for space after @ (ends mention)
      const afterAt = value.substring(lastAtIndex + 1, cursorPosition);
      const hasSpace = afterAt.includes(' ');

      // Space found, deactivate
      if (hasSpace) {
        resetMentionState();
        return;
      }

      // Extract query and activate mention
      const query = afterAt;

      // Set mention state after a small delay to avoid UI flickering
      setTimeout(() => {
        setMentionState({
          isActive: true,
          query,
          startPosition: lastAtIndex,
          endPosition: cursorPosition,
        });
      }, 100);
    },
    [resetMentionState],
  );

  // Filter users from store based on query
  const filterUsersFromStore = useCallback(
    (query: string) => {
      const normalizedQuery = normalizeQuery(query);

      // If query is empty (just @), show all users
      if (!normalizedQuery) {
        setFilteredUsers(users);
        return;
      }

      // Filter users from store data
      const filtered = users?.filter((user) => {
        const username = user.username?.toLowerCase() || '';

        return username.includes(normalizedQuery);
      });

      setFilteredUsers(filtered);
    },
    [users],
  );

  // Filter users when mention active and query changes
  useEffect(() => {
    // If mention is not active, clear filtered users
    if (!mentionState.isActive) {
      clearFilteredUsers();
      return;
    }

    filterUsersFromStore(mentionState.query);
  }, [
    mentionState.isActive,
    mentionState.query,
    filterUsersFromStore,
    clearFilteredUsers,
  ]);

  const checkForExactMatch = useCallback(() => {
    if (
      !mentionState.isActive ||
      !mentionState.query ||
      filteredUsers.length === 0
    ) {
      return null;
    }

    return findExactUsernameMatch(filteredUsers, mentionState.query);
  }, [mentionState.isActive, mentionState.query, filteredUsers]);

  // Reset selected index when filtered users change
  useEffect(() => {
    setSelectedUserIndex(-1);
  }, [filteredUsers]);

  // Create selection result with formatted names
  const selectUser = useCallback(
    (user: UserProfile) => ({
      user,
      displayName: getUserMentionText(user),
      mentionText: `@${getUserMentionText(user)}`,
      replaceStart: mentionState.startPosition,
      replaceEnd: mentionState.endPosition,
    }),
    [mentionState],
  );

  // Handle keyboard events: arrows, enter, escape, space
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!mentionState.isActive || filteredUsers.length === 0) return false;

      switch (event.key) {
        case KEYBOARD_EVENT.ARROW_DOWN:
          event.preventDefault();
          // Navigate down with circular wrap: last → first
          setSelectedUserIndex((prev) => {
            const newIndex = prev < filteredUsers.length - 1 ? prev + 1 : 0;

            // Ensures DOM updates before scrolling to new selected item
            setTimeout(() => scrollSelectedUserIntoView(), 0);
            return newIndex;
          });
          return true;

        case KEYBOARD_EVENT.ARROW_UP:
          event.preventDefault();
          // Navigate up with circular wrap: first → last
          setSelectedUserIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : filteredUsers.length - 1;

            // Ensures DOM updates before scrolling to new selected item
            setTimeout(() => scrollSelectedUserIntoView(), 0);
            return newIndex;
          });
          return true;

        case KEYBOARD_EVENT.ENTER:
          // Select highlighted user
          if (selectedUserIndex >= 0 && filteredUsers[selectedUserIndex]) {
            event.preventDefault();
            return selectUser(filteredUsers[selectedUserIndex]);
          }
          return false;

        case KEYBOARD_EVENT.ESCAPE:
          event.preventDefault();

          // Close dropdown
          resetMentionState();
          return true;

        case KEYBOARD_EVENT.SPACE:
          // Auto-select if exact username match exists
          if (mentionState.query && filteredUsers.length > 0) {
            const exactMatch = findExactUsernameMatch(
              filteredUsers,
              mentionState.query,
            );

            if (exactMatch) {
              event.preventDefault();
              return selectUser(exactMatch);
            }
          }

          // No match, close dropdown
          resetMentionState();
          return false;

        default:
          return false;
      }
    },
    [
      mentionState.isActive,
      mentionState.query,
      filteredUsers,
      selectedUserIndex,
      selectUser,
      scrollSelectedUserIntoView,
      resetMentionState,
    ],
  );

  // Close dropdown and reset all state
  const closeMention = useCallback(() => {
    resetMentionState();
    clearFilteredUsers();
    setSelectedUserIndex(-1);
  }, [resetMentionState, clearFilteredUsers]);

  return {
    // State
    users: filteredUsers,
    isLoading,
    selectedUserIndex,
    isMentionActive: mentionState.isActive,
    mentionQuery: mentionState.query,

    // Refs
    selectedUserRef,
    dropdownRef,

    // Methods
    detectMention,
    selectUser,
    handleKeyDown,
    closeMention,
    checkForExactMatch,
    updateSelectedUserIndex: setSelectedUserIndex,
  };
};
